/**
 * oracleFacts — real answers to factual F1 questions.
 * --------------------------------------------------------------------------
 * Why this exists
 * ---------------
 * Asked "who won the last race?", the Oracle used to reply with a fully
 * detailed report of a 2026 Miami Grand Prix won by Verstappen: a top-five
 * classification, gaps to three decimal places, a fastest lap, and two
 * paragraphs of race narrative. None of it happened. It came from the mock
 * response catalogue, which exists to demo the chat UI, and it was presented
 * in exactly the same voice as a real answer.
 *
 * That is the worst failure mode this app has. The Results tab, one tap away,
 * has had the real classification for every race since 2000 the whole time.
 *
 * So factual questions are answered from the same services the Results screen
 * uses. When those cannot answer, the Oracle says so. A result question must
 * NEVER fall through to the mock catalogue — "I could not retrieve that" is a
 * worse answer than the truth and a far better one than a convincing lie.
 *
 * Scope is deliberately narrow: race results and championship standings. Those
 * are the two factual lookups a user is most likely to try, and both map onto
 * services that already exist. Everything else still routes as before.
 */

import { fetchAvailableTracks, fetchRaceResults } from './resultsService.js';
import {
  fetchDriverStandings,
  fetchConstructorStandings,
  fetchSeasonRaces,
  fetchQualifying,
  fetchSeasonDrivers,
  fetchSeasonConstructors,
  fetchDriverSeasonResults,
  fetchConstructorSeasonResults,
  driverDisplayName,
  combineDateTime,
  parseStatus,
  EARLIEST_SEASON,
} from './jolpicaClient.js';

/** Phrases that make a message a request for a race classification. */
const RACE_RESULT_PATTERNS = [
  /\bwho\s+(won|win|wins)\b/,
  /\bwinner\s+of\b/,
  /\b(last|latest|previous|most recent)\s+(race|grand prix|gp)\b/,
  /\brace\s+(result|results|winner|report|recap)\b/,
  /\brecap\b.*\b(gp|grand prix|race)\b/,
  /\b(gp|grand prix)\b.*\brecap\b/,
  /\bhow did\b.*\b(race|gp|grand prix)\b.*\b(go|end|finish)\b/,
];

/** Phrases that make a message a request for the championship table. */
const STANDINGS_PATTERNS = [
  /\b(current|latest)?\s*(driver|drivers|constructor|constructors|championship)\s+standings\b/,
  /\bstandings\b/,
  /\bchampionship\s+(lead|leader|table|position|battle)\b/,
  // "who's" has no word break before the apostrophe, and the typographic
  // apostrophe (’) arrives from phone keyboards, so both forms are spelled out.
  /\bwho(\s+is|'s|’s)\s+(leading|winning|top)\b/,
  /\bhow many points\b/,
  /\bpoints\s+(table|gap)\b/,
];

/** Phrases that make a message a request for the weekend timetable. */
const SCHEDULE_PATTERNS = [
  /\b(next|upcoming)\s+(race|grand prix|gp|round|session|weekend|qualifying|sprint)\b/,
  // "when's the next race", "when is the monaco gp", "when does qualifying start".
  // The alternation omits "was"/"did", so past-tense result questions don't match.
  /\bwhen(?:'s|’s|\s+is|\s+are|\s+does|\s+do)\b[^?]*\b(next|race|grand prix|gp|round|start|begin|session|qualifying|sprint|weekend)\b/,
  /\bwhat\s+time\b[^?]*\b(race|start|begin|quali|qualifying|sprint|session|lights)\b/,
  /\brace\s+(schedule|calendar|weekend|start time|time)\b/,
  /\b(schedule|calendar|timetable)\b[^?]*\b(race|weekend|grand prix|gp|round)\b/,
  /\bhow\s+(long|many days)\s+until\b[^?]*\b(race|grand prix|gp|round)\b/,
  /\bwhere\s+can\s+i\s+watch\b/,
  /\blights\s+out\b/,
];

/** Phrases that make a message a request for qualifying / the grid. */
const QUALIFYING_PATTERNS = [
  /\bpole\s*(position|sitter)?\b/,
  /\bqualif(?:y|ying|ied)\b/,
  /\bquali\b/,
  /\bqualy\b/,
  /\bq3\b/,
  /\bgrid\s+(position|order|slot|penalty|spot)\b/,
  /\bfront\s+row\b/,
  /\bstarting\s+(grid|position|order)\b/,
];

/** Phrases that make a message a two-driver comparison. */
const HEAD_TO_HEAD_PATTERNS = [
  /\b(?:vs\.?|versus)\b/,
  /\bhead[-\s]?to[-\s]?head\b/,
  /\bcompare\b[^?]*\band\b/,
  /\bwho(?:'s|’s| is)\s+(better|ahead|faster|winning)\b/,
  /\bteam[-\s]?mates?\b/,
];

/** Phrases that ask how a driver's or team's season is going. */
const SEASON_SUMMARY_PATTERNS = [
  /\bhow(?:'s|’s|\s+is|\s+are|\s+has|\s+have|\s+did|\s+'?s)\b[^?]*\b(season|year|doing|going|performed|performing|been|form|campaign)\b/,
  /\b(season|year)\s+(summary|recap|so far|form|review|overview)\b/,
  /\b(form|campaign)\s+(this|so far|so-far)\b/,
];

/** Question is about constructors rather than drivers. */
const CONSTRUCTOR_PATTERN = /\b(constructor|constructors|team|teams)\b/;

export const FACT_INTENTS = {
  RACE_RESULT: 'race-result',
  STANDINGS: 'standings',
  SCHEDULE: 'schedule',
  QUALIFYING: 'qualifying',
  HEAD_TO_HEAD: 'head-to-head',
  SEASON_SUMMARY: 'season-summary',
};

/**
 * detectFactIntent — which factual lookup, if any, this message is asking for.
 *
 * Order encodes precedence for genuinely ambiguous phrasings:
 *   - SCHEDULE before QUALIFYING: "when's the next qualifying" is a timetable
 *     question, not a request for a past grid.
 *   - QUALIFYING before RACE_RESULT: "who won qualifying" is about the grid, and
 *     the RACE_RESULT "who won" pattern would otherwise claim it.
 *   - RACE_RESULT before STANDINGS: "who won" is a result even when the sentence
 *     also mentions the championship.
 */
export function detectFactIntent(userInput) {
  const text = String(userInput ?? '').toLowerCase();
  if (SCHEDULE_PATTERNS.some((re) => re.test(text))) return FACT_INTENTS.SCHEDULE;
  if (QUALIFYING_PATTERNS.some((re) => re.test(text))) return FACT_INTENTS.QUALIFYING;
  if (HEAD_TO_HEAD_PATTERNS.some((re) => re.test(text))) return FACT_INTENTS.HEAD_TO_HEAD;
  if (SEASON_SUMMARY_PATTERNS.some((re) => re.test(text))) return FACT_INTENTS.SEASON_SUMMARY;
  if (RACE_RESULT_PATTERNS.some((re) => re.test(text))) return FACT_INTENTS.RACE_RESULT;
  if (STANDINGS_PATTERNS.some((re) => re.test(text))) return FACT_INTENTS.STANDINGS;
  return null;
}

/**
 * parseSeason — an explicit four-digit season in the question, if any, clamped
 * to what the data source actually covers. Returns null for "no year given",
 * which the callers read as "the current season".
 */
export function parseSeason(userInput, now = new Date()) {
  const currentYear = now.getUTCFullYear();
  const matches = String(userInput ?? '').match(/\b(19|20)\d{2}\b/g);
  if (!matches) return null;

  // Last mentioned year wins: "the 2021 vs 2022 title fight" is about 2022.
  const year = parseInt(matches[matches.length - 1], 10);
  if (year < EARLIEST_SEASON || year > currentYear) return null;
  return year;
}

/** Circuit-name words that identify no particular circuit. */
const CIRCUIT_STOPWORDS = new Set([
  'circuit', 'circuito', 'autodromo', 'autódromo', 'autodrome', 'international',
  'national', 'raceway', 'speedway', 'park', 'street', 'grand', 'prix', 'the',
  'de', 'del', 'di', 'do', 'of', 'and',
]);

/**
 * matchTrack — find the race the question names, if it names one.
 *
 * Matches the short name ("Belgian"), the full circuit name, the country and
 * the locality — plus the distinctive words *inside* a full circuit name, so
 * "who won at Spa-Francorchamps" resolves even though nobody types "Circuit de
 * Spa-Francorchamps". Longest candidate first, so "United States" is not
 * shadowed by a stray "States".
 *
 * Matching is on word boundaries rather than substrings: plain `includes`
 * would let a three-letter locality like "Spa" match "Spain" and "space".
 */
export function matchTrack(userInput, tracks) {
  const text = String(userInput ?? '').toLowerCase();
  const candidates = [];

  const add = (value, track) => {
    const name = String(value ?? '').toLowerCase().trim();
    if (name.length >= 3) candidates.push({ name, track });
  };

  for (const track of tracks) {
    add(track.circuit_full_name, track);
    add(track.circuit_short_name, track);
    add(track.country_name, track);
    add(track.location, track);

    for (const word of String(track.circuit_full_name ?? '').toLowerCase().split(/[^a-zà-ÿ0-9]+/)) {
      if (word.length >= 5 && !CIRCUIT_STOPWORDS.has(word)) add(word, track);
    }
  }

  candidates.sort((a, b) => b.name.length - a.name.length);

  const hit = candidates.find(({ name }) => {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, 'i').test(text);
  });
  return hit?.track ?? null;
}

/** Most recent race with a classification, i.e. the one "the last race" means. */
function latestConcluded(tracks) {
  const concluded = tracks.filter((t) => t.isConcluded && !t.is_cancelled);
  return concluded.length > 0 ? concluded[concluded.length - 1] : null;
}

function ordinalGap(row, index) {
  if (index === 0) return 'winner';
  const gap = String(row.time_gap ?? '').trim();
  if (!gap || gap === '-') return row.status ?? '';
  return gap;
}

/**
 * fetchRaceFacts — real classification for the race the question is about.
 *
 * @returns {Promise<{race, tracks, track, season}|null>} null only when the
 *          season has no concluded races at all.
 */
export async function fetchRaceFacts(userInput, { signal, now = new Date() } = {}) {
  const season = parseSeason(userInput, now) ?? now.getUTCFullYear();
  let tracks = await fetchAvailableTracks(season, { signal });

  // Early in a new season nothing has run yet — "the last race" then means the
  // final race of the previous one, which is what a user actually wants.
  if (tracks.length === 0 || !latestConcluded(tracks)) {
    if (parseSeason(userInput, now) !== null) return { season, tracks, track: null, race: null };
    tracks = await fetchAvailableTracks(season - 1, { signal });
    if (!latestConcluded(tracks)) return null;
    return finishRaceFacts(userInput, tracks, season - 1, { signal });
  }

  return finishRaceFacts(userInput, tracks, season, { signal });
}

async function finishRaceFacts(userInput, tracks, season, { signal }) {
  const named = matchTrack(userInput, tracks);
  const track = named ?? latestConcluded(tracks);
  if (!track) return { season, tracks, track: null, race: null };

  // A named race that has not run yet is a legitimate answer in itself.
  if (!track.isConcluded) return { season, tracks, track, race: null, upcoming: true };

  const race = await fetchRaceResults(track.id, tracks, { signal });
  return { season, tracks, track, race };
}

/**
 * fetchStandingsFacts — real championship table, drivers or constructors.
 */
export async function fetchStandingsFacts(userInput, { signal, now = new Date() } = {}) {
  const text = String(userInput ?? '').toLowerCase();
  const wantsConstructors = CONSTRUCTOR_PATTERN.test(text);
  let season = parseSeason(userInput, now) ?? now.getUTCFullYear();

  const load = (year) =>
    wantsConstructors
      ? fetchConstructorStandings(year, { signal })
      : fetchDriverStandings(year, { signal });

  let result = await load(season);

  // A season that has not started yet returns an empty table; fall back to the
  // completed one rather than reporting "no standings".
  if ((result.standings ?? []).length === 0 && parseSeason(userInput, now) === null) {
    season -= 1;
    result = await load(season);
  }

  const rows = (result.standings ?? []).map((entry) => ({
    position: entry.position ?? null,
    points: parseFloat(entry.points) || 0,
    wins: parseInt(entry.wins, 10) || 0,
    name: wantsConstructors
      ? entry.Constructor?.name ?? 'Unknown'
      : driverDisplayName(entry.Driver),
    team: wantsConstructors ? null : entry.Constructors?.[entry.Constructors.length - 1]?.name ?? '',
  }));

  return { season, wantsConstructors, round: result.round ?? null, rows };
}

/**
 * describeRace — human summary line for a completed race. Kept here so the
 * response builder stays presentational.
 */
export function describeRace({ track, race }) {
  const winner = race?.results?.[0];
  if (!winner) return null;

  const laps = Number.isFinite(Number(winner.laps)) ? `${winner.laps} laps` : null;
  const second = race.results[1];
  const marginText = second && second.time_gap && second.time_gap !== '-'
    ? ` ${second.driver_name} finished second, ${second.time_gap}.`
    : '';

  return (
    `${winner.driver_name} won the ${track.season} ${track.circuit_short_name} Grand Prix for ` +
    `${winner.team_name}${laps ? `, over ${laps}` : ''}.${marginText}`
  );
}

/* ===========================================================================
 * Qualifying — real grid classification
 * ========================================================================= */

/** The race a question names, else the latest concluded one. */
function pickTrack(userInput, tracks) {
  return matchTrack(userInput, tracks) ?? latestConcluded(tracks);
}

/**
 * resolveRaceContext — the season + specific race a question is scoped to,
 * WITHOUT fetching any classification. Mirrors fetchRaceFacts' resolution
 * (explicit year vs current season, roll back to last season before the
 * opener) so a qualifying answer and a race-result answer agree on which race
 * "the last one" is. Kept separate from fetchRaceFacts so that battle-tested
 * path is untouched.
 */
async function resolveRaceContext(userInput, { signal, now = new Date() } = {}) {
  const explicit = parseSeason(userInput, now);
  const season = explicit ?? now.getUTCFullYear();
  const tracks = await fetchAvailableTracks(season, { signal });

  if ((tracks.length === 0 || !latestConcluded(tracks)) && explicit === null) {
    const prev = await fetchAvailableTracks(season - 1, { signal });
    if (latestConcluded(prev)) {
      return { season: season - 1, tracks: prev, track: pickTrack(userInput, prev) };
    }
  }
  return { season, tracks, track: pickTrack(userInput, tracks) };
}

/**
 * fetchQualifyingFacts — real qualifying classification for the race a question
 * names (or the latest concluded one). Row 0 is pole. `time` is the driver's
 * best segment (Q3, else Q2, else Q1).
 */
export async function fetchQualifyingFacts(userInput, { signal, now = new Date() } = {}) {
  const { season, track } = await resolveRaceContext(userInput, { signal, now });
  if (!track) return { season, track: null, rows: [] };
  if (!track.isConcluded) return { season, track, upcoming: true, rows: [] };

  const race = await fetchQualifying(season, track.round, { signal });
  const rows = (race?.QualifyingResults ?? []).map((q) => ({
    position: parseInt(q.position, 10) || null,
    name: driverDisplayName(q.Driver),
    team: q.Constructor?.name ?? '',
    time: q.Q3 || q.Q2 || q.Q1 || '-',
    segment: q.Q3 ? 'Q3' : q.Q2 ? 'Q2' : q.Q1 ? 'Q1' : null,
  }));
  return { season, track, rows };
}

/* ===========================================================================
 * Schedule — the next race and its weekend timetable
 * ========================================================================= */

/**
 * fetchScheduleFacts — the next race (or a race the question names), with the
 * raw Jolpica Race object so the presenter can read its session times. Rolls
 * into next season's opener during the winter break, when the current calendar
 * is finished.
 */
export async function fetchScheduleFacts(userInput, { signal, now = new Date() } = {}) {
  const explicit = parseSeason(userInput, now);
  let season = explicit ?? now.getUTCFullYear();
  let races = await fetchSeasonRaces(season, { signal });

  const named = matchScheduledRace(userInput, races);
  if (named) return { season, race: named, named: true };

  let race = nextRace(races, now);
  if (!race && explicit === null) {
    // Off-season: this calendar is done — roll forward to next year's opener.
    season += 1;
    races = await fetchSeasonRaces(season, { signal });
    race = nextRace(races, now) ?? races[0] ?? null;
  }
  return { season, race, named: false };
}

/** Earliest race whose start is now-or-later. */
function nextRace(races, now) {
  const t = now.getTime();
  return (
    races
      .map((r) => ({ r, start: raceStart(r) }))
      .filter((x) => x.start && x.start.getTime() >= t)
      .sort((a, b) => a.start - b.start)[0]?.r ?? null
  );
}

function raceStart(race) {
  const iso = combineDateTime(race.date, race.time);
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
}

/**
 * matchScheduledRace — reuse matchTrack against the raw calendar by shaping each
 * Jolpica Race into the field names matchTrack reads. Returns the raw Race so
 * the presenter still has the session timetable.
 */
function matchScheduledRace(userInput, races) {
  const shaped = races.map((r) => ({
    circuit_full_name: r.Circuit?.circuitName,
    circuit_short_name: (r.raceName ?? '').replace(/\s+Grand Prix$/i, ''),
    country_name: r.Circuit?.Location?.country,
    location: r.Circuit?.Location?.locality,
    _race: r,
  }));
  return matchTrack(userInput, shaped)?._race ?? null;
}

/* ===========================================================================
 * Head-to-head + season summary — resolve an entrant, then read their season
 *
 * These are the two intents that need entity resolution: a free-text
 * "Hamilton" / "Ferrari" mapped to a driverId / constructorId. When nothing
 * resolves, the fetchers return null so the router can keep going (KB, general
 * answer) rather than dead-end — a mis-detected "who's better at strategy?"
 * should not become "I couldn't find that driver."
 * ========================================================================= */

/** Strip diacritics so "Raikkonen" matches "Räikkönen" and "Perez" matches "Pérez". */
function deburr(value) {
  return String(value).normalize('NFD').replace(/\p{Diacritic}/gu, '');
}

/**
 * boundaryIndex — word-boundary match index for `name` in `text`, or -1. Not a
 * substring test (so "Sainz" never matches inside another word). `text` must
 * already be lower-cased and deburred; the name is deburred here.
 */
function boundaryIndex(text, name) {
  const escaped = deburr(name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const m = new RegExp(`(^|[^a-z0-9])(${escaped})([^a-z0-9]|$)`, 'i').exec(text);
  return m ? m.index + m[1].length : -1;
}

/** Ways a driver might be named: full name, family name, a long given name, TLA code. */
function driverCandidates(driver) {
  const out = [];
  const full = driverDisplayName(driver).toLowerCase();
  const family = (driver.familyName ?? '').toLowerCase();
  const given = (driver.givenName ?? '').toLowerCase();
  const code = (driver.code ?? '').toLowerCase();
  if (full.length >= 3) out.push(full);
  if (family.length >= 3) out.push(family);
  if (given.length >= 4) out.push(given); // "Max" (3) is too collision-prone alone
  if (code.length === 3) out.push(code);
  return out;
}

/**
 * matchDrivers — the drivers a message names, in the order they appear (so
 * "Hamilton vs Verstappen" returns [Hamilton, Verstappen]). Longest candidate
 * wins per driver, so a full name beats a stray shared given name.
 */
export function matchDrivers(userInput, drivers, { limit = 2 } = {}) {
  const text = deburr(String(userInput ?? '').toLowerCase());
  const candidates = [];
  for (const d of drivers) {
    for (const name of driverCandidates(d)) candidates.push({ name, driver: d });
  }
  candidates.sort((a, b) => b.name.length - a.name.length);

  const hits = [];
  const seen = new Set();
  for (const { name, driver } of candidates) {
    if (seen.has(driver.driverId)) continue;
    const idx = boundaryIndex(text, name);
    if (idx >= 0) {
      hits.push({ driver, idx });
      seen.add(driver.driverId);
    }
  }
  hits.sort((a, b) => a.idx - b.idx);
  return hits.slice(0, limit).map((h) => h.driver);
}

/** Trailing team words that carry no identity ("Alpine F1 Team" → "Alpine"). */
const CONSTRUCTOR_SUFFIX = /\s+(?:f1\s+team|racing|team|f1)$/i;

function constructorCandidates(constructor) {
  const name = (constructor.name ?? '').toLowerCase();
  const out = new Set();
  if (name.length >= 3) out.add(name);
  const stripped = name.replace(CONSTRUCTOR_SUFFIX, '').trim();
  if (stripped.length >= 3) out.add(stripped);
  return [...out];
}

/** matchConstructor — the team a message names, longest candidate first. */
export function matchConstructor(userInput, constructors) {
  const text = deburr(String(userInput ?? '').toLowerCase());
  const candidates = [];
  for (const c of constructors) {
    for (const name of constructorCandidates(c)) candidates.push({ name, constructor: c });
  }
  candidates.sort((a, b) => b.name.length - a.name.length);
  for (const { name, constructor } of candidates) {
    if (boundaryIndex(text, name) >= 0) return constructor;
  }
  return null;
}

/**
 * resolveSeason — the season a "this season" question is about: an explicit
 * year if given, else the current season once it has a completed race, else
 * the one before (so an off-season query answers about the year that just ran).
 */
async function resolveSeason(userInput, { signal, now = new Date() } = {}) {
  const explicit = parseSeason(userInput, now);
  if (explicit !== null) return explicit;
  const year = now.getUTCFullYear();
  const tracks = await fetchAvailableTracks(year, { signal });
  return latestConcluded(tracks) ? year : year - 1;
}

/** Sort key: classified finishers by position, everyone else after. */
function finishRank(result) {
  if (!result) return Infinity;
  const status = parseStatus(result.status);
  const pos = parseInt(result.position, 10);
  if ((status === 'Finished' || status === 'Lapped') && Number.isFinite(pos)) return pos;
  return 100 + (Number.isFinite(pos) ? pos : 99);
}

/** Season tallies from a driver's (or one car's) race rows. */
function summariseResults(races) {
  let entries = 0;
  let wins = 0;
  let podiums = 0;
  let dnfs = 0;
  let points = 0;
  let bestFinish = null;

  for (const race of races) {
    for (const result of race.Results ?? []) {
      entries++;
      points += parseFloat(result.points) || 0;
      const status = parseStatus(result.status);
      const pos = parseInt(result.position, 10);
      if ((status === 'Finished' || status === 'Lapped') && Number.isFinite(pos)) {
        if (bestFinish === null || pos < bestFinish) bestFinish = pos;
        if (pos === 1) wins++;
        if (pos <= 3) podiums++;
      } else if (status === 'DNF' || status === 'DSQ') {
        dnfs++;
      }
    }
  }
  return { entries, wins, podiums, dnfs, points: Math.round(points * 100) / 100, bestFinish };
}

/** Race-day head-to-head across the rounds both drivers started. */
function raceHeadToHead(racesA, racesB) {
  const byRound = new Map(racesB.map((r) => [r.round, r]));
  let aAhead = 0;
  let bAhead = 0;
  let shared = 0;
  for (const race of racesA) {
    const other = byRound.get(race.round);
    if (!other) continue;
    const ax = finishRank(race.Results?.[0]);
    const bx = finishRank(other.Results?.[0]);
    if (!Number.isFinite(ax) || !Number.isFinite(bx)) continue;
    shared++;
    if (ax < bx) aAhead++;
    else if (bx < ax) bAhead++;
  }
  return { aAhead, bAhead, shared };
}

/** The two drivers with the most entries in a constructor's season (the pairing). */
function topTwoDrivers(constructorRaces) {
  const counts = new Map();
  for (const race of constructorRaces) {
    for (const r of race.Results ?? []) {
      const d = r.Driver;
      if (!d?.driverId) continue;
      const seen = counts.get(d.driverId);
      if (seen) seen.n++;
      else counts.set(d.driverId, { driver: d, n: 1 });
    }
  }
  return [...counts.values()].sort((a, b) => b.n - a.n).slice(0, 2).map((x) => x.driver);
}

/** Distinct driver display names across a constructor's season, in first-seen order. */
function constructorDriverNames(constructorRaces) {
  const seen = new Map();
  for (const race of constructorRaces) {
    for (const r of race.Results ?? []) {
      const d = r.Driver;
      if (d?.driverId && !seen.has(d.driverId)) seen.set(d.driverId, driverDisplayName(d));
    }
  }
  return [...seen.values()];
}

/** The team a driver most recently raced for in a season. */
function driverTeamName(driverRaces) {
  for (let i = driverRaces.length - 1; i >= 0; i--) {
    const name = driverRaces[i].Results?.[0]?.Constructor?.name;
    if (name) return name;
  }
  return '';
}

/**
 * fetchHeadToHeadFacts — race-day head-to-head and season tallies for the two
 * drivers a message names. "Ferrari's team-mates" resolves to that team's two
 * cars. Returns null when a pair can't be identified.
 */
export async function fetchHeadToHeadFacts(userInput, { signal, now = new Date() } = {}) {
  const season = await resolveSeason(userInput, { signal, now });

  const drivers = await fetchSeasonDrivers(season, { signal });
  let pair = matchDrivers(userInput, drivers, { limit: 2 });

  if (pair.length < 2) {
    // Team-mate framing: "how do the Ferrari drivers compare?"
    const constructors = await fetchSeasonConstructors(season, { signal });
    const team = matchConstructor(userInput, constructors);
    if (team) {
      const teamRaces = await fetchConstructorSeasonResults(season, team.constructorId, { signal });
      pair = topTwoDrivers(teamRaces);
    }
  }
  if (pair.length < 2) return null;

  const [driverA, driverB] = pair;
  const [racesA, racesB] = await Promise.all([
    fetchDriverSeasonResults(season, driverA.driverId, { signal }),
    fetchDriverSeasonResults(season, driverB.driverId, { signal }),
  ]);

  return {
    season,
    a: { name: driverDisplayName(driverA), ...summariseResults(racesA) },
    b: { name: driverDisplayName(driverB), ...summariseResults(racesB) },
    h2h: raceHeadToHead(racesA, racesB),
  };
}

/**
 * fetchSeasonSummaryFacts — a driver's or team's season: wins, podiums, points,
 * best finish, DNFs, and championship position. Tries a driver first, then a
 * constructor. Returns null when neither resolves.
 */
export async function fetchSeasonSummaryFacts(userInput, { signal, now = new Date() } = {}) {
  const season = await resolveSeason(userInput, { signal, now });

  const drivers = await fetchSeasonDrivers(season, { signal });
  const driver = matchDrivers(userInput, drivers, { limit: 1 })[0];
  if (driver) {
    const races = await fetchDriverSeasonResults(season, driver.driverId, { signal });
    const standing = await driverChampionshipPosition(season, driver.driverId, { signal });
    return {
      season,
      kind: 'driver',
      name: driverDisplayName(driver),
      team: driverTeamName(races),
      summary: summariseResults(races),
      standing,
    };
  }

  const constructors = await fetchSeasonConstructors(season, { signal });
  const team = matchConstructor(userInput, constructors);
  if (team) {
    const races = await fetchConstructorSeasonResults(season, team.constructorId, { signal });
    const standing = await constructorChampionshipPosition(season, team.constructorId, { signal });
    return {
      season,
      kind: 'constructor',
      name: team.name,
      drivers: constructorDriverNames(races),
      summary: summariseResults(races),
      standing,
    };
  }

  return null;
}

async function driverChampionshipPosition(season, driverId, { signal }) {
  try {
    const { standings } = await fetchDriverStandings(season, { signal });
    const row = standings.find((s) => s.Driver?.driverId === driverId);
    return row?.position ? parseInt(row.position, 10) : null;
  } catch {
    return null; // a missing standing just omits the line
  }
}

async function constructorChampionshipPosition(season, constructorId, { signal }) {
  try {
    const { standings } = await fetchConstructorStandings(season, { signal });
    const row = standings.find((s) => s.Constructor?.constructorId === constructorId);
    return row?.position ? parseInt(row.position, 10) : null;
  } catch {
    return null;
  }
}

export { latestConcluded, ordinalGap };
